import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { supportedCountriesISO2Selector } from 'modules/app/appSlice';
import { RootState } from 'store';
import { isAfter, parseISO } from 'date-fns';

interface State {
  subscriptionObjects: SubscriptionObject[];
}

const initialState: State = {
  subscriptionObjects: [],
};

const slice = createSlice({
  name: 'subscriptions',
  initialState,
  reducers: {
    setSubscriptions: (state: State, action: PayloadAction<SubscriptionObject[]>): State => {
      state.subscriptionObjects = action.payload;

      return state;
    },

    updateSubscription: (state: State, action: PayloadAction<SubscriptionObject>): State => {
      const subscriptionIdx = state.subscriptionObjects.findIndex(subscription => subscription.id === action.payload.id);
      if (typeof subscriptionIdx !== 'undefined' && subscriptionIdx > -1) {
        state.subscriptionObjects[subscriptionIdx] = action.payload;
      }
      return state;
    },

    createSubscription: (state: State, action: PayloadAction<SubscriptionObject>): State => {
      state.subscriptionObjects.push(action.payload);

      return state;
    },
  },
});

const { setSubscriptions, createSubscription, updateSubscription } = slice.actions;

export { setSubscriptions, createSubscription, updateSubscription };
export default slice.reducer;

export const employeeBenefitSubscription = createSelector(
  (state: RootState) => state.subscriptions.subscriptionObjects,
  subscriptions =>
    subscriptions?.find(
      (subscription: SubscriptionObject) =>
        subscription.type === 'EmployeeBenefit' && subscription.category === 'Employee benefit' && subscription.status === 'Active',
    ),
);

export const incompleteEmployeeBenefitSubscription = createSelector(
  (state: RootState) => state.subscriptions.subscriptionObjects,
  subscriptions =>
    subscriptions?.find(
      (subscription: SubscriptionObject) =>
        subscription.type === 'EmployeeBenefit' && subscription.category === 'Employee benefit' && subscription.status === 'Incomplete',
    ),
);
export const carLoanSubscription = createSelector(
  (state: RootState) => state.subscriptions.subscriptionObjects,
  subscriptions =>
    subscriptions?.find(
      (subscription: SubscriptionObject) => subscription.type === 'Car' && subscription.category === 'Car loan' && subscription.status === 'Active',
    ),
);

export const employeeBenefitTotalKilosCO2 = createSelector(
  employeeBenefitSubscription,
  incompleteEmployeeBenefitSubscription,
  (activeSubscription, nonRecurringSubscription) => {
    const subscription = nonRecurringSubscription ? nonRecurringSubscription : activeSubscription;
    return subscription?.items?.reduce<number>((prev, next) => (prev += next.kilosCo2), 0);
  },
);

export const employeeBenefitAmountOfPeople = createSelector(
  employeeBenefitSubscription,
  incompleteEmployeeBenefitSubscription,
  (activeSubscription, nonRecurringSubscription) => {
    const subscription = nonRecurringSubscription ? nonRecurringSubscription : activeSubscription;
    return subscription?.items?.reduce<number>((prev, next) => (prev += next.details.person?.personCount ?? 0), 0);
  },
);

export const employeeBenefitAvailableCountries = createSelector(
  employeeBenefitSubscription,
  incompleteEmployeeBenefitSubscription,
  (activeSubscription, nonRecurringSubscription) => {
    const subscription = nonRecurringSubscription ? nonRecurringSubscription : activeSubscription;
    return subscription?.items.map(item => ({ label: item.details.person?.countryName, value: item.details.person?.countryCode }));
  },
);

export const employeeBenefitSubscriptionStartDate = createSelector(
  employeeBenefitSubscription,
  incompleteEmployeeBenefitSubscription,
  (activeSubscription, nonRecurringSubscription) => {
    const subscription = nonRecurringSubscription ? nonRecurringSubscription : activeSubscription;
    return subscription?.startDate ?? '';
  },
);

export const employeeBenefitSelectedPlan = createSelector(
  employeeBenefitSubscription,
  incompleteEmployeeBenefitSubscription,
  (activeSubscription, nonRecurringSubscription) => {
    const subscription = nonRecurringSubscription ? nonRecurringSubscription : activeSubscription;
    return subscription?.items ? subscription.items[0]?.details.person?.plan ?? '' : '';
  },
);

export const employeeBenefitSortedCountriesWithSeats = createSelector(
  [employeeBenefitAvailableCountries, supportedCountriesISO2Selector, (state: RootState): Seats | undefined => state.allUsers.seats],
  (employeeBenefitCountries, allCountries, seats) => {
    return allCountries
      .sort(country => {
        if (employeeBenefitCountries?.find(availableCountry => availableCountry.value === country.value)) {
          return -1;
        } else {
          return 1;
        }
      })
      .map(country => {
        const availableCountry = seats?.offsetSeatsByCountry.find(offsetCountry => offsetCountry.countryCodeIso2 === country.value);
        if (availableCountry) {
          return {
            value: country.value,
            label: `${country.label} (${availableCountry.available})`,
          };
        } else {
          return country;
        }
      });
  },
);

export const carLoansTotalKilosCO2 = createSelector(carLoanSubscription, subscription =>
  subscription?.items?.reduce<number>((prev, next) => (prev += next.kilosCo2), 0),
);

export const carLoansAmountOfCars = createSelector(carLoanSubscription, subscription =>
  subscription?.items?.reduce<number>((prev, next) => (prev += next.details.car?.cars ?? 0), 0),
);

export const lifestyleSubscriptionSelector = createSelector(
  (state: RootState) => state.subscriptions.subscriptionObjects,
  subscriptions =>
    subscriptions?.find((subscription: SubscriptionObject) => subscription.type === 'PersonalLifestyle' && subscription.category === 'Lifestyle'),
);

export const longestActiveLifestyleSubscriptionSelector = createSelector(
  (state: RootState) => state.subscriptions.subscriptionObjects,
  subscriptions => {
    const sortedActiveLifestyleSubs = subscriptions
      ?.filter(
        (subscription: SubscriptionObject) =>
          subscription.type === 'PersonalLifestyle' && subscription.category === 'Lifestyle' && subscription.status === 'Active',
      )
      .sort((prev, next) => (isAfter(parseISO(prev.startDate ?? ''), parseISO(next.startDate ?? '')) ? 1 : -1));

    return sortedActiveLifestyleSubs.length ? sortedActiveLifestyleSubs[0] : undefined;
  },
);

export const personalCarSubscriptionSelector = createSelector(
  (state: RootState) => state.subscriptions.subscriptionObjects,
  subscriptions => subscriptions?.find((subscription: SubscriptionObject) => subscription.type === 'PersonalCar' && subscription.category === 'Car'),
);

export const petSubscriptionSelector = createSelector(
  (state: RootState) => state.subscriptions.subscriptionObjects,
  subscriptions =>
    subscriptions?.find((subscription: SubscriptionObject) => subscription.type === 'PersonalPets' && subscription.category === 'Pets'),
);

export const lifestyleSubscriptionStartDate = createSelector(lifestyleSubscriptionSelector, subscription => {
  return subscription?.startDate ?? '';
});

export const lifestyleLongestActiveSubscriptionStartDate = createSelector(longestActiveLifestyleSubscriptionSelector, subscription => {
  return subscription?.status === 'Active' ? subscription?.startDate ?? '' : '';
});

export const lifestyleLongestSelectedPlan = createSelector(longestActiveLifestyleSubscriptionSelector, subscription => {
  return subscription?.items ? subscription.items[0]?.details.person?.plan ?? '' : '';
});

export const wasEmployeeBenefitSubscriptionModified = createSelector(employeeBenefitSubscription, subscription =>
  isAfter(new Date(subscription?.modifiedDate ?? ''), new Date(subscription?.createdDate ?? '')),
);

export const wasLongestLifestyleSubscriptionModified = createSelector(longestActiveLifestyleSubscriptionSelector, subscription =>
  isAfter(new Date(subscription?.modifiedDate ?? ''), new Date(subscription?.createdDate ?? '')),
);

export const allLifestyleSubscriptions = createSelector(
  (state: RootState) => state.subscriptions.subscriptionObjects,
  subscriptions =>
    subscriptions?.filter((subscription: SubscriptionObject) => subscription.type === 'PersonalLifestyle' && subscription.category === 'Lifestyle') ??
    [],
);

export const allSubscriptions = createSelector(
  (state: RootState) => state.subscriptions,
  subscriptions => subscriptions.subscriptionObjects,
);

const byStartDateAscending = <T extends SubscriptionObject>(prev: T, next: T): number =>
  isAfter(parseISO(prev.startDate ?? ''), parseISO(next.startDate ?? '')) ? 1 : -1;

export const sortSubscriptions = <T extends SubscriptionObject>(subs: T[]): T[] => {
  const inactiveSubs = subs.filter(sub => sub.status !== 'Active').sort(byStartDateAscending);
  const activeSubs = subs.filter(sub => sub.status === 'Active').sort(byStartDateAscending);

  return [...activeSubs, ...inactiveSubs];
};

const toIndexedSubscriptionObject = (sub: SubscriptionObject, index: number): IndexedSubscriptionObject => ({ ...sub, index });

const indexedLifestyleSubscriptions = createSelector(allLifestyleSubscriptions, subs => subs.map(toIndexedSubscriptionObject));

export const allLifestyleSubscriptionsSortedIndexed = createSelector(indexedLifestyleSubscriptions, sortSubscriptions);
